import API from "serviceshift-ui/api-client";
import { computed, reactive } from "vue";

// A full page load replaces the softphone's iframe, which drops any call in it.
// Dialpad's dialer tells the page nothing about outbound calls, so before such a
// load Sera asks its server whether the logged-in Dialpad user, or a call
// answered in the dialer, is live. Only then is the user asked.
//
// A refresh, a typed address or a closed tab cannot wait for that answer, so
// Sera also keeps the answer current while the dialer is up, and only asks the
// browser to confirm leaving while it says a call is in progress.
//
// Module-level because the links live in the routed app and the dialer in the
// app header, which are separate Vue apps.

interface PendingLeave {
  href: string;
  go: () => void;
  canOpenInNewTab: boolean;
  // What the user is doing, as the warning names it: "Going to Technicians".
  action: string;
  // Shown under the warning, e.g. that a save already went through.
  note: string | null;
  onStay?: () => void;
}

interface LeaveOptions {
  canOpenInNewTab?: boolean;
  action?: string;
  note?: string | null;
  onStay?: () => void;
}

const LABEL_MAX = 40;

// "Going to <the link's text>", for the warning to name where the user is headed.
export function goingTo(link: Element | null): string {
  const label = (link?.textContent ?? "").replace(/\s+/g, " ").trim();
  if (!label) return "Going to this page";
  return `Going to ${label.length > LABEL_MAX ? `${label.slice(0, LABEL_MAX)}…` : label}`;
}

const state = reactive({
  dialpadUserId: null as string | null,
  answeredCallIds: [] as string[],
  // When Sera last placed a call through the dialer, in ms since the epoch.
  placedAt: null as number | null,
  checking: false,
  pending: null as PendingLeave | null,
  callInProgress: false,
  // Set once the user has agreed to leave, so the browser does not ask again.
  leaving: false,
  // Set once Sera's session is over, as when the user logged out in another tab.
  sessionEnded: false
});

// How often the answer is refreshed: often during a call, so the browser stops
// asking soon after it ends, and rarely otherwise.
export const ON_CALL_CHECK_MS = 5_000;
export const IDLE_CHECK_MS = 15_000;
const LEAVING_MS = 1_000;
// Dialpad's webhooks reach Sera a moment after the dialer acts, usually within a
// second, which the first check catches for a refresh soon after dialling.
export const FOLLOW_UP_CHECKS_MS = [1_000, 2_000, 5_000, 10_000];
// Sera's server may not have heard from Dialpad yet about a call it just placed,
// so it is told for this long and takes the call as live until it does. Dialpad
// sends word within a second or two, so none by then means no call was placed.
export const PLACED_CALL_MS = 30_000;

// Set by the dialer while a Dialpad user is logged in to it; null otherwise.
export function setDialpadUser(dialpadUserId: string | null) {
  if (dialpadUserId !== state.dialpadUserId) state.placedAt = null;
  state.dialpadUserId = dialpadUserId;
}

// Set by the dialer as it places a call Sera asked for. Until a check says
// otherwise, leaving would drop it.
export function notePlacedCall() {
  state.placedAt = Date.now();
  state.callInProgress = true;
  checkCallStatusSoon();
}

function placedSecondsAgo(): number | null {
  if (state.placedAt === null) return null;
  const elapsed = Date.now() - state.placedAt;
  if (elapsed > PLACED_CALL_MS) {
    state.placedAt = null;
    return null;
  }
  return Math.floor(elapsed / 1000);
}

// Set by the dialer with the incoming calls answered in it on this page.
export function setAnsweredCalls(callIds: readonly string[]) {
  state.answeredCallIds = [...callIds];
}

export function guardsLeaving(): boolean {
  if (state.sessionEnded) return false;
  return state.dialpadUserId !== null || state.answeredCallIds.length > 0;
}

// Once Sera's session is over the app sends the page to its login screen, and a
// call cannot be kept past that, so nothing here stands in the way any more.
export function endLeaveGuard() {
  state.sessionEnded = true;
  state.callInProgress = false;
  state.pending = null;
}

type CheckTrigger = "leave" | "poll";

// The server's answer, or null when it could not be had.
async function fetchActive(trigger: CheckTrigger): Promise<boolean | null> {
  const query = new URLSearchParams({ trigger });
  if (state.dialpadUserId) query.append("dialpad_user_id", state.dialpadUserId);
  state.answeredCallIds.forEach((id) => query.append("call_ids[]", id));
  const placedAgo = placedSecondsAgo();
  if (placedAgo !== null) query.append("placed_ago", String(placedAgo));

  try {
    const res = await API.makeRequest(
      `/v2/integration/dialpad/active_call?${query}`,
      { method: "GET" }
    );
    if (res?.data?.connected === false) {
      disconnectListeners.forEach((listener) => listener());
      return false;
    }
    const active = res?.data?.active;
    return typeof active === "boolean" ? active : null;
  } catch {
    return null;
  }
}

const disconnectListeners = new Set<() => void>();

// Called when a check finds Dialpad no longer connected for the tenant, so the
// softphone can hang up and go away. @return stops listening
export function onDialpadDisconnected(listener: () => void): () => void {
  disconnectListeners.add(listener);
  return () => disconnectListeners.delete(listener);
}

// When Sera cannot tell before a page load, it warns: an extra warning costs a
// click, a missed one costs the call.
async function onCall(): Promise<boolean> {
  const active = await fetchActive("leave");
  if (active !== null) state.callInProgress = active;
  return active ?? true;
}

let inFlight: Promise<void> | null = null;

export function refreshCallStatus(): Promise<void> {
  if (!guardsLeaving()) {
    state.callInProgress = false;
    return Promise.resolve();
  }
  // An answer that could not be had keeps the last one.
  inFlight ??= fetchActive("poll")
    .then((active) => {
      if (active !== null) state.callInProgress = active;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

// Right after the dialer places, answers or is used for a call.
export function checkCallStatusSoon() {
  FOLLOW_UP_CHECKS_MS.forEach((ms) => setTimeout(refreshCallStatus, ms));
}

// Keeps the answer current while the dialer is up. @return stops it
export function watchCallStatus(): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  state.leaving = false;

  const check = async () => {
    await refreshCallStatus();
    if (stopped) return;
    const delay = state.callInProgress ? ON_CALL_CHECK_MS : IDLE_CHECK_MS;
    timer = setTimeout(check, delay);
  };
  check();

  return () => {
    stopped = true;
    clearTimeout(timer);
    state.callInProgress = false;
  };
}

// Whether leaving now, by any means, would drop a call the user has not agreed
// to drop.
export function callInProgress(): boolean {
  return state.callInProgress && !state.leaving && !state.sessionEnded;
}

// Runs `go`, the full page load to `href`, right away unless it would drop a
// Dialpad call; then it waits for the user's answer in the leave warning. A
// form submission cannot be repeated in a new tab, so it offers no new tab.
export async function leavePage(
  href: string,
  go: () => void,
  {
    canOpenInNewTab = true,
    action = "Going to this page",
    note = null,
    onStay
  }: LeaveOptions = {}
) {
  if (!guardsLeaving()) return go();
  // A second click while the first is being checked is the same intent. A load
  // that has to be cleaned up when not taken, like a held Turbo page, is.
  if (state.checking || state.pending) {
    onStay?.();
    return;
  }

  state.checking = true;
  try {
    if (await onCall()) {
      state.pending = { href, go, canOpenInNewTab, action, note, onStay };
    } else go();
  } finally {
    state.checking = false;
  }
}

// The page a click would load in place of this one, or null when the click
// opens elsewhere, stays on the page or is handled by script. Mirrors the
// router's own click interceptor, which handles the Vue app's links first.
export function fullPageLoadHref(event: MouseEvent): string | null {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return null;
  }

  const link = (event.target as Element | null)?.closest?.("a");
  const href = link?.getAttribute("href");
  if (!link || !href || href.startsWith("#")) return null;
  if (link.target && link.target !== "_self") return null;
  if (link.hasAttribute("download")) return null;

  const scriptHandled = [
    "data-method",
    "data-turbo-frame",
    "data-action",
    "data-remote",
    "data-toggle"
  ];
  if (scriptHandled.some((attribute) => link.hasAttribute(attribute))) {
    return null;
  }

  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  const samePage =
    url.pathname === window.location.pathname &&
    url.search === window.location.search;
  return samePage && url.hash ? null : url.href;
}

// Where a submit would load a new page in place of this one, or null when it
// is handled by script, opens elsewhere or stays put.
export function fullPageSubmitAction(event: SubmitEvent): string | null {
  if (event.defaultPrevented) return null;
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return null;

  const submitter = event.submitter as HTMLButtonElement | null;
  const target = submitter?.getAttribute("formtarget") ?? form.target;
  if (target && target !== "_self") return null;
  const method = (
    submitter?.getAttribute("formmethod") ?? form.method
  ).toLowerCase();
  if (method === "dialog") return null;
  if (form.hasAttribute("data-remote") || form.hasAttribute("data-turbo-frame"))
    return null;

  const action = submitter?.getAttribute("formaction") ?? form.action;
  const url = new URL(action || window.location.href, window.location.href);
  return url.origin === window.location.origin ? url.href : null;
}

function takePending(): PendingLeave | null {
  const pending = state.pending;
  state.pending = null;
  return pending;
}

export function useLeaveWarning() {
  return {
    pendingHref: computed(() => state.pending?.href ?? null),
    canOpenInNewTab: computed(() => state.pending?.canOpenInNewTab ?? false),
    action: computed(() => state.pending?.action ?? ""),
    note: computed(() => state.pending?.note ?? null),
    leave: () => {
      const pending = takePending();
      if (!pending) return;
      state.leaving = true;
      pending.go();
      // The browser asks as the load starts. A Turbo save that swaps the page in
      // place never unloads it, and the next call must still be asked about.
      setTimeout(() => {
        state.leaving = false;
      }, LEAVING_MS);
    },
    openInNewTab: () => {
      const pending = takePending();
      if (pending?.canOpenInNewTab) {
        window.open(pending.href, "_blank", "noopener");
      }
    },
    stay: () => {
      takePending()?.onStay?.();
    }
  };
}
